import { ChangeDetectionStrategy, Component, HostBinding, Input, ViewChild } from '@angular/core';
import { filter, map, shareReplay, switchMap } from 'rxjs/operators';
import {
  Constants,
  Lookup,
  NEED_TO_KNOW_VISIBILITY_LEVEL_LOOKUP,
  NeedToKnow,
  NeedToKnowKeys,
  NeedToKnowVisibilityLevel,
  ChangeSourceType,
} from '@ag-common-lib/public-api';
import ArrayStore from 'devextreme/data/array_store';
import DataSource from 'devextreme/data/data_source';
import { BehaviorSubject, combineLatest, from, Observable } from 'rxjs';
import { AgentService } from '../../../../../services/agent.service/agent.service';
import { NeedToKnowModalComponent } from '../need-to-know-modal/need-to-know-modal.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { QueryParam, WhereFilterOperandKeys } from '../../../../../dao/CommonFireStoreDao.dao';
import { NeedToKnowConfig } from './need-to-know-grid.model';
import { AgentNeedToKnowService } from '../../../../../services/agent-need-to-know.service';
import { AuthService } from 'ag-common-svc/public-api';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';

type NeedToKnowGridState = {
  dataSource: DataSource;
  createdByLookup: Partial<Lookup>[];
};

@UntilDestroy()
@Component({
  selector: 'ag-shr-need-to-know-grid',
  templateUrl: './need-to-know-grid.component.html',
  styleUrls: ['./need-to-know-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NeedToKnowGridComponent {
  @HostBinding('class') className = 'need-to-know-grid';
  @ViewChild('needToKnowModalRef') needToKnowModalComponent: NeedToKnowModalComponent;
  @Input() set agentId(dbId: string) {
    this.agentId$.next(dbId);
    this.agentDbId = dbId ?? null;
  }
  @Input() title: string;
  @Input() isEditable: boolean = true;
  @Input() canEdit = true;
  @Input() canDelete = true;
  @Input() canCreate = true;
  @Input() sourceType: ChangeSourceType | null = null;
  @Input() extraToolbarItems = [];
  @Input() editModalOptions: NeedToKnowConfig;
  @Input() set allowedVisibilityLevels(data: NeedToKnowVisibilityLevel[]) {
    if (Array.isArray(data)) {
      this.allowedVisibilityLevels$.next(data);
    }
  }

  public BaseModelKeys = BaseModelKeys;
  public NeedToKnowKeys = NeedToKnowKeys;
  public needToKnowVisibilityLevelLookup = NEED_TO_KNOW_VISIBILITY_LEVEL_LOOKUP;
  public gridState$: Observable<NeedToKnowGridState>;

  private loggedInUserEmail: string;
  private readonly agentId$ = new BehaviorSubject<string>(undefined);
  public agentDbId: string | null = null;
  readonly dateFormat: string = Constants.DISPLAY_DATE_FORMAT;

  private allowedVisibilityLevels$ = new BehaviorSubject<NeedToKnowVisibilityLevel[]>([]);

  constructor(
    authService: AuthService,
    private agentService: AgentService,
    private agentNeedToKnowService: AgentNeedToKnowService,
  ) {
    authService.currentUser$
      ?.pipe(
        untilDestroyed(this),
        map(user => user?.email),
      )
      .subscribe(loggedInUserEmail => {
        this.loggedInUserEmail = loggedInUserEmail;
      });

    this.gridState$ = combineLatest([this.agentId$, this.allowedVisibilityLevels$]).pipe(
      filter(([agentId]) => !!agentId),
      switchMap(([agentId, allowedVisibilityLevels]) => {
        const qp: QueryParam[] = [];
        if (Array.isArray(allowedVisibilityLevels) && allowedVisibilityLevels?.length) {
          const visibilityLevelsQueryParam = new QueryParam(
            NeedToKnowKeys.visibilityLevel,
            WhereFilterOperandKeys.in,
            allowedVisibilityLevels,
          );
          qp.push(visibilityLevelsQueryParam);
        }

        return this.agentNeedToKnowService.getList(agentId, qp);
      }),
      switchMap(needToKnows => {
        const rows = Array.isArray(needToKnows) ? needToKnows : [];
        const createdByAgentIds = rows
          .map(row => row?.[BaseModelKeys.createdByAgentDbId])
          .filter((id): id is string => !!id);

        return from(this.agentService.getAgentsLookupByIds(createdByAgentIds)).pipe(
          map(createdByLookup => ({
            dataSource: new DataSource({
              store: new ArrayStore({
                key: 'dbId',
                data: rows,
              }),
            }),
            createdByLookup,
          })),
        );
      }),
      shareReplay(1),
    );
  }

  public onRowRemoving = e => {
    e.cancel = this.agentNeedToKnowService.delete(this.agentId$.value, e.data[BaseModelKeys.dbId], this.sourceType);
  };

  public showAddNeedToKnowPopup = () => {
    this.needToKnowModalComponent.showModal(
      this.agentId$.value,
      this.editModalOptions?.initialNeedToKnow as NeedToKnow,
      this.sourceType
    );
  };

  public showEditPopup = ({ row: { data } }) => {
    this.needToKnowModalComponent.showModal(this.agentId$.value, data, this.sourceType);
  };

  public getIsEditingAllowed = ({ row: { data } }) => {
    return !!this.loggedInUserEmail && this.loggedInUserEmail === data[BaseModelKeys.createdBy];
  };

}
